import { Button } from "@dmca/frontend/components/button";
import { PageHeader } from "@dmca/frontend/components/nav";
import {
  checkPermissionsInLoader,
  useAppPermissions,
} from "@dmca/frontend/hooks";
import { useTRPC } from "@dmca/frontend/trpc";
import { InventoryTable } from "@dmca/frontend/widgets/inventory";
import { DateTime } from "@dmca/utils/luxon";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { Paper, Stack } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { type ChangeEvent, useCallback } from "react";
import { toast } from "react-toastify";

const InventoryImportFileExtensions = [".xlsx", ".xls"];

export const Route = createFileRoute("/_authed/inventory")({
  component: RouteComponent,
  loader: async ({ context }) => {
    await checkPermissionsInLoader("Inventory::ViewInventory", context);
    const { trpcQcProxy: trpc, qc } = context;
    qc.prefetchQuery(trpc.inventory.getAll.queryOptions());
    qc.prefetchQuery(trpc.inventory.getLocations.queryOptions());
  },
  head: () => ({
    meta: [
      {
        title: "MRO Inventory | DNA Dashboard",
      },
    ],
  }),
});

function RouteComponent() {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const hasPermission = useAppPermissions();
  const exportMutation = useMutation(
    trpc.inventory.exportToXLSX.mutationOptions()
  );
  const importMutation = useMutation(
    trpc.inventory.importFromXLSX.mutationOptions({
      onSuccess: (rows) => {
        toast.success(
          `Imported ${rows.length} inventory item${
            rows.length === 1 ? "" : "s"
          }.`
        );
        qc.invalidateQueries(trpc.inventory.getAll.queryFilter());
        qc.invalidateQueries(trpc.inventory.getLocations.queryFilter());
      },
      onError: (e) => {
        toast.error(
          `Error importing inventory: ${e.message || "Unknown error"}`
        );
      },
    })
  );

  // Converts the base64 file returned by tRPC into a browser download
  const downloadInventoryExport = useCallback(async (data: string) => {
    const paddedBase64 = data
      .padEnd(data.length + ((4 - (data.length % 4 || 4)) % 4), "=")
      .replace(/-/g, "+")
      .replace(/_/g, "/");
    const response = await fetch(
      `data:application/octet-stream;base64,${paddedBase64}`
    );
    const blob = new Blob([await response.arrayBuffer()], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Inventory (${DateTime.now().toFormat("MMM d yyyy")}).xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, []);

  const handleExport = useCallback(() => {
    exportMutation.mutate(undefined, {
      onError: (e) => {
        toast.error(
          `Error exporting inventory: ${e.message || "Unknown error"}`
        );
      },
      onSuccess: async ({ data }) => {
        await downloadInventoryExport(data);
        toast.success("Inventory backup exported.");
      },
    });
  }, [downloadInventoryExport, exportMutation]);

  const backupThenImport = useCallback(
    (base64: string) => {
      exportMutation.mutate(undefined, {
        onError: (e) => {
          toast.error(
            `Inventory backup failed: ${e.message || "Unknown error"}`
          );
        },
        onSuccess: async ({ data }) => {
          await downloadInventoryExport(data);
          toast.success("Inventory backup exported.");
          importMutation.mutate({ file: base64 });
        },
      });
    },
    [downloadInventoryExport, exportMutation, importMutation]
  );

  const handleFileChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = "";

      if (!file) {
        return;
      }

      if (
        !InventoryImportFileExtensions.some((extension) =>
          file.name.toLowerCase().endsWith(extension)
        )
      ) {
        toast.error("Inventory import must be an .xlsx or .xls file.");
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        const base64 = (event.target?.result as string | undefined)?.split(
          ","
        )[1];

        if (!base64) {
          toast.error("Unknown error reading the file.");
          return;
        }

        backupThenImport(base64);
      };
      reader.onerror = () => {
        toast.error("Unknown error reading the file.");
      };
      reader.readAsDataURL(file);
    },
    [backupThenImport]
  );

  return (
    <>
      <PageHeader>MRO Inventory</PageHeader>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
      >
        <Paper sx={{ p: 2, width: "100%" }}>
          <Stack direction="row" gap={2}>
            {hasPermission("Inventory::ManageInventory") &&
              hasPermission("Inventory::ExportInventory") && (
                <Button
                  data-testid="inventory-main-page-export"
                  startIcon={<FileDownloadIcon />}
                  onClick={handleExport}
                  loading={exportMutation.isPending}
                >
                  Export
                </Button>
              )}
            {hasPermission("Inventory::ManageInventory") && (
              <>
                <Button
                  data-testid="inventory-main-page-import"
                  component="label"
                  startIcon={<UploadFileIcon />}
                  loading={exportMutation.isPending || importMutation.isPending}
                >
                  Import
                  <VisuallyHiddenInput
                    type="file"
                    accept={InventoryImportFileExtensions.join(",")}
                    onChange={handleFileChange}
                  />
                </Button>
              </>
            )}
          </Stack>
        </Paper>
      </Stack>
      <InventoryTable data-testid="inventory-main-page-jobs-table" />
    </>
  );
}

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});
